import { PanelErrorBoundary } from "@/components/panel-error-boundary"
import { StalledOrdersPanel } from "./stalled-orders-panel"
import { Link } from "@tanstack/react-router"
import { Activity, CircleCheck, CirclePause, TriangleAlert } from "lucide-react"
import type { DashboardOverview, DashboardStage } from "@optimist/schemas"
import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { formatDateTime } from "@/lib/utils"
import { formatOrderStatusBadgeLabel } from "@/features/orders/order-status"

const stageLabels: Record<DashboardStage, string> = {
	intake: "Intake",
	payment: "Payment",
	labs: "Labs",
	consultation: "Consultation",
	fulfillment: "Fulfillment",
	refills: "Refills",
}

export function DashboardOverviewContent({
	data,
	onPageChange,
	busy,
	onPreview,
}: {
	data: DashboardOverview
	onPageChange: (page: number) => void
	busy: boolean
	onPreview: (orderId: string) => void
}) {
	return (
		<div className="space-y-6 font-inter">
			<PanelErrorBoundary title="Order summary">
				<MetricsPanel data={data} />
			</PanelErrorBoundary>
			<div className="grid items-start gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
				<div className="min-w-0 space-y-6">
					<PanelErrorBoundary title="Blockers requiring attention">
						<BlockersPanel
							data={data}
							onPageChange={onPageChange}
							busy={busy}
							onPreview={onPreview}
						/>
					</PanelErrorBoundary>
					<PanelErrorBoundary title="Orders not progressing">
						<StalledOrdersPanel onPreview={onPreview} />
					</PanelErrorBoundary>
					<PanelErrorBoundary title="Other exceptions">
						<ExceptionsPanel data={data} />
					</PanelErrorBoundary>
				</div>
				<div className="min-w-0 space-y-6">
					<PanelErrorBoundary title="Order progress">
						<ProgressPanel data={data} />
					</PanelErrorBoundary>
					<PanelErrorBoundary title="Order outcomes">
						<OutcomesPanel data={data} />
					</PanelErrorBoundary>
				</div>
			</div>
		</div>
	)
}

function BlockersPanel({
	data,
	onPageChange,
	busy,
	onPreview,
}: {
	data: DashboardOverview
	onPageChange: (page: number) => void
	busy: boolean
	onPreview: (id: string) => void
}) {
	const totalPages = Math.max(
		1,
		Math.ceil(data.attention.total / data.attention.pageSize),
	)
	return (
		<section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
			<div className="border-b border-border-subtle px-6 py-5">
				<h2 className="text-section-title font-semibold">
					Blockers requiring attention
				</h2>
				<p className="mt-1 text-meta text-muted-foreground">
					Blockers, integration failures and holds · oldest update first
				</p>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-left text-data text-foreground-secondary [&_td]:whitespace-nowrap">
					<thead className="bg-muted text-label uppercase text-subtle-foreground">
						<tr>
							{[
								"Order",
								"Name",
								"Current status",
								"Blocker",
								"Last updated",
							].map((label) => (
								<th key={label} scope="col" className="px-6 py-3 font-semibold">
									{label}
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-border-subtle">
						{data.attention.orders.map((order) => (
							<tr
								key={order.id}
								onClick={() => onPreview(order.id)}
								className="cursor-pointer hover:bg-muted focus-within:bg-muted"
							>
								<td className="px-6 py-3">
									<Link
										to="/dashboard/orders/$orderId"
										params={{ orderId: order.id }}
										onClick={(event) => event.stopPropagation()}
										className="font-mono text-meta text-brand-foreground underline-offset-4 hover:underline"
										aria-label={`View order ${order.id}`}
										title={order.id}
									>
										{order.id.slice(0, 8)}…
									</Link>
								</td>
								<td className="px-6 py-3">
									<button
										type="button"
										className="text-left hover:underline focus-visible:outline-ring"
										aria-label={`Preview order ${order.id}`}
										onClick={(event) => {
											event.stopPropagation()
											onPreview(order.id)
										}}
									>
										{order.patientName || "—"}
									</button>
								</td>
								<td className="px-6 py-3">
									{order.onHold ? (
										<StatusBadge variant="warning">On hold</StatusBadge>
									) : (
										formatOrderStatusBadgeLabel(order.status)
									)}
								</td>
								<td className="px-6 py-3">
									{[
										order.blocked
											? order.integrationFailed
												? "Integration failed"
												: "Active blocker"
											: null,
										order.onHold ? "Active fill on hold" : null,
									]
										.filter((reason) => reason !== null)
										.join("; ")}
								</td>
								<td className="whitespace-nowrap px-6 py-3 text-meta tabular-nums">
									{formatDateTime(order.updatedAt)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{data.attention.orders.length === 0 ? (
				<p className="px-6 py-8 text-center text-body text-muted-foreground">
					{data.attention.total === 0
						? "No active blockers or visible fill holds."
						: "No orders on this page. Return to the previous page."}
				</p>
			) : null}
			<div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-6 py-3">
				<p className="text-meta text-muted-foreground">
					{data.attention.total} affected orders · Page {data.attention.page} of{" "}
					{totalPages}
				</p>
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={busy || data.attention.page <= 1}
						onClick={() => onPageChange(data.attention.page - 1)}
					>
						Previous
					</Button>
					<Button
						variant="outline"
						size="sm"
						disabled={busy || data.attention.page >= totalPages}
						onClick={() => onPageChange(data.attention.page + 1)}
					>
						Next
					</Button>
				</div>
			</div>
		</section>
	)
}

function ExceptionsPanel({ data }: { data: DashboardOverview }) {
	return (
		<section className="rounded-2xl border border-border bg-card shadow-card">
			<div className="border-b border-border-subtle px-6 py-5">
				<h2 className="text-section-title font-semibold">Other exceptions</h2>
				<p className="mt-1 text-meta text-muted-foreground">
					Active orders · may also appear in the blocker queue
				</p>
			</div>
			<dl className="divide-y divide-border-subtle px-6">
				{[
					{
						label: "Failed payments",
						count: data.exceptions.failedPayments,
					},
					{
						label: "Lab failures or rejected results",
						count: data.exceptions.labFailures,
					},
					{
						label: "Pharmacy failures",
						count: data.exceptions.pharmacyFailures,
					},
				].map(({ label, count }) => (
					<div
						key={label}
						className="flex items-center justify-between gap-3 py-4 text-data text-foreground-secondary"
					>
						<dt>{label}</dt>
						<dd className="tabular-nums">
							{count === null ? (
								<span className="text-meta text-muted-foreground">
									Restricted
								</span>
							) : (
								count.toLocaleString()
							)}
						</dd>
					</div>
				))}
			</dl>
		</section>
	)
}

function ProgressPanel({ data }: { data: DashboardOverview }) {
	return (
		<section className="rounded-2xl border border-border bg-card shadow-card">
			<div className="border-b border-border-subtle px-6 py-5">
				<h2 className="text-section-title font-semibold">Order progress</h2>
				<p className="mt-1 text-meta text-muted-foreground">
					Active orders by current status group
				</p>
			</div>
			<div className="space-y-4 p-6">
				{data.stages.map(({ stage, count }) => (
					<div
						key={stage}
						className="grid grid-cols-[7rem_1fr_2.5rem] items-center gap-3 text-data text-foreground-secondary"
					>
						<span>{stageLabels[stage]}</span>
						<progress
							aria-label={`${stageLabels[stage]} orders`}
							max={Math.max(data.inProgress, 1)}
							value={count}
							className="h-2 w-full overflow-hidden rounded-full border-0 bg-muted [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-control-foreground [&::-moz-progress-bar]:bg-control-foreground"
						/>
						<span className="text-right tabular-nums">
							{count.toLocaleString()}
						</span>
					</div>
				))}
			</div>
		</section>
	)
}

function OutcomesPanel({ data }: { data: DashboardOverview }) {
	return (
		<section className="rounded-2xl border border-border bg-card shadow-card">
			<div className="border-b border-border-subtle px-6 py-5">
				<h2 className="text-section-title font-semibold">Order outcomes</h2>
				<p className="mt-1 text-meta text-muted-foreground">
					Terminal workflow statuses · all time
				</p>
			</div>
			<dl className="divide-y divide-border-subtle px-6">
				{data.outcomes.map(({ status, count }) => (
					<div
						key={status}
						className="flex items-center justify-between gap-3 py-3 text-data text-foreground-secondary"
					>
						<dt className="capitalize">
							{formatOrderStatusBadgeLabel(status)}
						</dt>
						<dd className="tabular-nums">
							{status === "COMPLETED" ? (
								<StatusBadge variant="success">
									{count.toLocaleString()}
								</StatusBadge>
							) : (
								count.toLocaleString()
							)}
						</dd>
					</div>
				))}
			</dl>
		</section>
	)
}

function MetricsPanel({ data }: { data: DashboardOverview }) {
	const metrics = [
		{
			label: "Orders in progress",
			count: data.inProgress,
			Icon: Activity,
			tone: "bg-brand-muted text-brand-foreground",
			note: "Across all workflows",
		},
		{
			label: "Active blockers",
			count: data.activeBlockers,
			Icon: TriangleAlert,
			tone: "bg-amber-100 text-amber-800",
			note: "Recorded blockers or integration failures",
		},
		{
			label: "Active fills on hold",
			count: data.onHold,
			Icon: CirclePause,
			tone: "bg-amber-100 text-amber-800",
			note: "Active orders only",
		},
		{
			label: "Completed orders",
			count: data.completed,
			Icon: CircleCheck,
			tone: "bg-emerald-100 text-emerald-800",
			note: "Current status · all time",
		},
	]
	return (
		<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
			{metrics.map(({ label, count, Icon, tone, note }) => (
				<section
					key={label}
					className="rounded-2xl border border-border bg-card p-5 shadow-card"
				>
					<div className="flex items-center justify-between gap-3">
						<h2 className="text-body font-medium text-foreground-secondary">
							{label}
						</h2>
						<span
							className={`flex size-8 shrink-0 items-center justify-center rounded-full ${tone}`}
						>
							<Icon aria-hidden="true" className="size-4" />
						</span>
					</div>
					<p
						className={
							count === null
								? "mt-2 text-body text-muted-foreground"
								: "mt-2 text-metric font-semibold tabular-nums text-heading"
						}
					>
						{count === null ? "Restricted" : count.toLocaleString()}
					</p>
					<p className="mt-1 text-meta text-muted-foreground">
						{count === null ? "Requires clinical data access" : note}
					</p>
				</section>
			))}
		</div>
	)
}
